import { useMemo } from 'react';

const WINDOW_DOT_COLORS = ['#FCA5A5', '#FCD34D', '#86EFAC'];

/**
 * Browser chrome mockup: traffic lights, address bar, and gradient content card.
 *
 * @param {object} props
 * @param {object} props.theme - from getSiteTheme()
 * @param {string} props.hostname - shown in the address bar (e.g. rb.all-completed.com)
 * @param {string} props.title - main heading inside the card
 * @param {string} props.subtitle - supporting line under the title
 * @param {string} [props.eyebrow='All Completed'] - small uppercase label above the title
 * @param {string} props.accent - CSS background for the card (usually theme.projectAccentA/B)
 */
function BrowserView({
  theme,
  hostname,
  title,
  subtitle,
  eyebrow = 'All Completed',
  accent
}) {
  const styles = useMemo(() => getBrowserViewStyles(theme), [theme]);

  return (
    <div style={styles.previewShell} role="region" aria-label={`Browser preview: ${hostname}`}>
      <div style={styles.previewTop}>
        {WINDOW_DOT_COLORS.map((c) => (
          <span key={c} style={styles.dot(c)} />
        ))}
        <div style={styles.addressBar}>{hostname}</div>
      </div>

      <div style={styles.previewBody}>
        <div style={{ ...styles.previewHero, background: accent }}>
          <div style={styles.previewEyebrow}>{eyebrow}</div>
          <div style={styles.previewTitle}>{title}</div>
          <div style={styles.previewText}>{subtitle}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * @param {object} theme
 * @returns {Record<string, object | ((color: string) => object)>}
 */
function getBrowserViewStyles(theme) {
  return {
    previewShell: {
      borderRadius: 20,
      overflow: 'hidden',
      border: `1px solid ${theme.border}`,
      background: theme.cardBg,
      boxShadow: theme.shadow
    },
    previewTop: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      background: theme.previewTopBg,
      padding: '12px 14px'
    },
    dot: (color) => ({
      width: 10,
      height: 10,
      borderRadius: 999,
      background: color,
      opacity: 0.9
    }),
    addressBar: {
      marginLeft: 10,
      flex: 1,
      height: 26,
      borderRadius: 10,
      border: `1px solid ${theme.border}`,
      background: theme.addressBarBg,
      color: theme.muted,
      fontSize: 12,
      padding: '0 10px',
      display: 'flex',
      alignItems: 'center'
    },
    previewBody: {
      padding: 18,
      background: theme.previewTopBg
    },
    previewHero: {
      borderRadius: 18,
      padding: 28,
      minHeight: 220,
      color: '#F8FAFC',
      boxShadow: theme.shadow,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end'
    },
    previewEyebrow: {
      fontSize: 12,
      fontWeight: 800,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      opacity: 0.82
    },
    previewTitle: {
      marginTop: 10,
      fontSize: 34,
      fontWeight: 900,
      letterSpacing: '-0.03em'
    },
    previewText: {
      marginTop: 12,
      fontSize: 14,
      lineHeight: 1.6,
      opacity: 0.94,
      maxWidth: 320
    }
  };
}

export default BrowserView;
